import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaImage, FaTrash, FaEdit, FaSave, FaTimes, FaHome, FaFolderOpen, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import API_BASE_URL from '../config';
import './Admin.css';

const PhotoManager = () => {
  const { t } = useLanguage();
  const [photoItems, setPhotoItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [editingPhotoItem, setEditingPhotoItem] = useState(null);
  const [editingAlbumPhoto, setEditingAlbumPhoto] = useState(null); // { photoId, albumId }
  const [editingAlbum, setEditingAlbum] = useState(null); // albumId для редактирования альбома
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [uploadToAlbumId, setUploadToAlbumId] = useState('');
  const [createNewAlbumForUpload, setCreateNewAlbumForUpload] = useState(false);
  const [newAlbumNameForUpload, setNewAlbumNameForUpload] = useState('');
  const [newAlbumDescriptionForUpload, setNewAlbumDescriptionForUpload] = useState('');
  const [photoForm, setPhotoForm] = useState({
    title: '',
    description: '',
    images: []
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [homepageMainImageId, setHomepageMainImageId] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]); // Массив ID выбранных фото для массового удаления
  const [selectAllMode, setSelectAllMode] = useState(false); // Режим выбора всех фото
  const [showMoveDialog, setShowMoveDialog] = useState(false); // Показать диалог переноса

  useEffect(() => {
    fetchPhotoItems();
    fetchAlbums();
    fetchHomepageMainImage();
  }, []);

  const fetchHomepageMainImage = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/homepage/main-image`);
      if (response.data && response.data.galleryItemId) {
        setHomepageMainImageId(response.data.galleryItemId);
      } else {
        setHomepageMainImageId(null);
      }
    } catch (error) {
      console.error('Error fetching homepage main image:', error);
      setHomepageMainImageId(null);
    }
  };

  const handleSetHomepageMainImage = async (photoItemId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/homepage/main-image`, { galleryItemId: photoItemId });
      setHomepageMainImageId(photoItemId);
      alert(t('admin.homepageImageSet') || 'Главное фото установлено!');
    } catch (error) {
      console.error('Error setting homepage main image:', error);
      alert(t('admin.homepageImageError') || 'Ошибка установки главного фото');
    }
  };

  const fetchPhotoItems = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/gallery`);
      // Фильтруем только изображения
      const imagesOnly = response.data.filter(item => item.type === 'image');
      setPhotoItems(imagesOnly);
    } catch (error) {
      console.error('Error fetching photo items:', error);
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/albums`);
      // Фильтруем только альбомы с фото (игнорируем альбомы только с видео)
      const albumsWithPhotos = response.data.filter(album => 
        album.photos && album.photos.length > 0
      );
      setAlbums(albumsWithPhotos);
    } catch (error) {
      console.error('Error fetching albums:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    
    if (photoForm.images.length === 0) {
      alert(t('admin.selectFiles') || 'Выберите фото для загрузки');
      return;
    }

    let targetAlbumId = uploadToAlbumId;

    // Если альбом не выбран и не создается новый - автоматически создаем новый альбом
    if (!targetAlbumId && !createNewAlbumForUpload) {
      // Создаем альбом с названием на основе даты и первого фото
      const date = new Date();
      const dateStr = date.toLocaleDateString('ru-RU', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const firstFileName = photoForm.images[0]?.name?.replace(/\.[^/.]+$/, '') || 'Фото';
      const autoAlbumName = `${dateStr} - ${firstFileName}`;
      
      try {
        setUploading(true);
        const albumResponse = await axios.post(`${API_BASE_URL}/api/albums`, {
          name: autoAlbumName,
          description: `Альбом создан автоматически ${dateStr}`
        });
        targetAlbumId = albumResponse.data.id;
        await fetchAlbums();
        alert(t('admin.albumCreatedAuto') || `Создан новый альбом: ${autoAlbumName}`);
      } catch (error) {
        console.error('Error creating auto album:', error);
        alert(t('admin.albumCreateError') || 'Ошибка создания альбома');
        setUploading(false);
        return;
      }
    } else if (createNewAlbumForUpload) {
      // Создаем альбом с указанным названием
      if (!newAlbumNameForUpload.trim()) {
        alert(t('admin.fillTitle') || 'Введите название альбома');
        return;
      }

      try {
        setUploading(true);
        const albumResponse = await axios.post(`${API_BASE_URL}/api/albums`, {
          name: newAlbumNameForUpload.trim(),
          description: newAlbumDescriptionForUpload.trim()
        });
        targetAlbumId = albumResponse.data.id;
        await fetchAlbums();
      } catch (error) {
        console.error('Error creating album:', error);
        alert(t('admin.albumCreateError') || 'Ошибка создания альбома');
        setUploading(false);
        return;
      }
    }

    // Загружаем фото в альбом
    if (!targetAlbumId) {
      alert(t('admin.selectAlbum') || 'Выберите альбом или создайте новый');
      setUploading(false);
      return;
    }

    try {
      const formData = new FormData();
      photoForm.images.forEach(file => {
        formData.append('photos', file);
      });

      await axios.post(`${API_BASE_URL}/api/albums/${targetAlbumId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await fetchAlbums();
      
      setPhotoForm({ title: '', description: '', images: [] });
      setImagePreviews([]);
      setUploadToAlbumId('');
      setCreateNewAlbumForUpload(false);
      setNewAlbumNameForUpload('');
      setNewAlbumDescriptionForUpload('');
      
      alert(t('admin.photosUploaded') || 'Фото загружены!');
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert(t('admin.photosUploadError') || 'Ошибка загрузки фото');
    } finally {
      setUploading(false);
    }
  };

  const handleImageFilesChange = (e) => {
    const files = Array.from(e.target.files);
    setPhotoForm({ ...photoForm, images: files });
    
    if (files.length === 0) {
      setImagePreviews([]);
      return;
    }
    
    const previews = [];
    let loadedCount = 0;
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews[index] = { file, preview: reader.result };
        loadedCount++;
        if (loadedCount === files.length) {
          setImagePreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDeletePhotoItem = async (id) => {
    if (!window.confirm(t('admin.deletePhotoConfirm') || 'Удалить это фото?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/gallery/${id}`);
      
      if (homepageMainImageId === id) {
        try {
          await axios.delete(`${API_BASE_URL}/api/homepage/main-image`);
          setHomepageMainImageId(null);
        } catch (error) {
          console.error('Error removing homepage main image:', error);
        }
      }
      
      // Удаляем ID из массива выбранных фото
      setSelectedPhotos(prev => prev.filter(photoId => photoId !== id));
      
      fetchPhotoItems();
    } catch (error) {
      console.error('Error deleting photo item:', error);
      alert(t('admin.photoDeleteError') || 'Ошибка удаления фото');
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    const name = document.getElementById('album-name')?.value.trim();
    const description = document.getElementById('album-description')?.value.trim() || '';
    
    if (!name) {
      alert(t('admin.fillTitle') || 'Введите название альбома');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/albums`, {
        name,
        description
      });
      await fetchAlbums(); // Обновляем список альбомов
      setShowAlbumForm(false);
      if (document.getElementById('album-name')) document.getElementById('album-name').value = '';
      if (document.getElementById('album-description')) document.getElementById('album-description').value = '';
      alert(t('admin.albumCreated') || 'Альбом создан!');
    } catch (error) {
      console.error('Error creating album:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(t('admin.albumCreateError') || 'Ошибка создания альбома: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAlbum = async (id) => {
    if (!window.confirm(t('admin.deleteAlbumConfirm') || 'Удалить этот альбом? Все фото будут удалены.')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/albums/${id}`);
      setAlbums(albums.filter(a => a.id !== id));
      if (selectedAlbum === id) {
        setSelectedAlbum(null);
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert(t('admin.albumDeleteError') || 'Ошибка удаления альбома');
    }
  };

  const handleUpdateAlbum = async (albumId) => {
    try {
      const nameInput = document.getElementById(`album-name-${albumId}`);
      const descriptionInput = document.getElementById(`album-description-${albumId}`);
      
      const name = nameInput?.value.trim();
      const description = descriptionInput?.value.trim() || '';

      if (!name) {
        alert(t('admin.fillTitle') || 'Заполните название альбома');
        return;
      }

      const response = await axios.put(`${API_BASE_URL}/api/albums/${albumId}`, {
        name,
        description
      });

      setAlbums(albums.map(a => a.id === albumId ? response.data : a));
      setEditingAlbum(null);
      alert(t('admin.albumUpdated') || 'Альбом обновлен!');
    } catch (error) {
      console.error('Error updating album:', error);
      alert(t('admin.albumUpdateError') || 'Ошибка обновления альбома');
    }
  };

  const handleUploadPhotosToAlbum = async (albumId, files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('photos', file);
    });

    try {
      setUploading(true);
      await axios.post(`${API_BASE_URL}/api/albums/${albumId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchAlbums();
      if (selectedAlbum === albumId) {
        const updatedAlbums = await axios.get(`${API_BASE_URL}/api/albums`);
        setAlbums(updatedAlbums.data);
      }
      alert(t('admin.photosUploaded') || 'Фото загружены!');
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert(t('admin.photosUploadError') || 'Ошибка загрузки фото');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhotoFromAlbum = async (albumId, photoId) => {
    if (!window.confirm(t('admin.deletePhotoConfirm') || 'Удалить это фото?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/albums/${albumId}/photos/${photoId}`);
      
      // Удаляем ID из массива выбранных фото
      setSelectedPhotos(prev => prev.filter(id => id !== photoId));
      
      fetchAlbums();
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert(t('admin.photoDeleteError') || 'Ошибка удаления фото');
    }
  };

  const handleSavePhotoItem = async (id) => {
    try {
      const title = document.getElementById(`photo-title-${id}`).value.trim();
      const description = document.getElementById(`photo-description-${id}`).value.trim();

      if (!title) {
        alert(t('admin.fillTitle'));
        return;
      }

      await axios.put(`${API_BASE_URL}/api/gallery/${id}`, {
        title,
        description
      });

      setEditingPhotoItem(null);
      fetchPhotoItems();
    } catch (error) {
      console.error('Error updating photo item:', error);
      alert(t('admin.photoUpdateError') || 'Ошибка обновления фото');
    }
  };

  const handleSaveAlbumPhoto = async (albumId, photoId) => {
    try {
      const title = document.getElementById(`album-photo-title-${photoId}`)?.value.trim();
      const description = document.getElementById(`album-photo-description-${photoId}`)?.value.trim();
      const newAlbumId = document.getElementById(`album-photo-album-${photoId}`)?.value;

      if (!title) {
        alert(t('admin.fillTitle') || 'Введите название');
        return;
      }

      const updateData = {
        title,
        description: description || ''
      };

      // Если выбран другой альбом, добавляем newAlbumId для переноса
      if (newAlbumId && newAlbumId !== albumId) {
        updateData.newAlbumId = newAlbumId;
      }

      await axios.put(`${API_BASE_URL}/api/albums/${albumId}/photos/${photoId}`, updateData);

      setEditingAlbumPhoto(null);
      await fetchAlbums();
      
      // Если фото было перенесено в другой альбом, обновляем selectedAlbum
      if (newAlbumId && newAlbumId !== albumId) {
        setSelectedAlbum(newAlbumId);
        alert(t('admin.photoMoved') || 'Фото перенесено в другой альбом!');
      } else {
        alert(t('admin.photoUpdated') || 'Фото обновлено!');
      }
    } catch (error) {
      console.error('Error updating album photo:', error);
      alert(t('admin.photoUpdateError') || 'Ошибка обновления фото');
    }
  };

  const handleTogglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleSelectAllPhotos = () => {
    if (selectedAlbum) {
      const album = albums.find(a => a.id === selectedAlbum);
      if (album && album.photos.length > 0) {
        if (selectAllMode) {
          setSelectedPhotos([]);
          setSelectAllMode(false);
        } else {
          setSelectedPhotos(album.photos.map(p => p.id));
          setSelectAllMode(true);
        }
      }
    } else if (photoItems.length > 0) {
      if (selectAllMode) {
        setSelectedPhotos([]);
        setSelectAllMode(false);
      } else {
        setSelectedPhotos(photoItems.map(p => p.id));
        setSelectAllMode(true);
      }
    }
  };

  const handleBulkDeletePhotos = async () => {
    if (selectedPhotos.length === 0) {
      alert(t('admin.selectPhotosToDelete') || 'Выберите фото для удаления');
      return;
    }

    if (!window.confirm(t('admin.deleteSelectedPhotosConfirm') || `Удалить ${selectedPhotos.length} выбранных фото?`)) return;

    try {
      if (selectedAlbum) {
        // Удаление фото из альбома
        for (const photoId of selectedPhotos) {
          await axios.delete(`${API_BASE_URL}/api/albums/${selectedAlbum}/photos/${photoId}`);
        }
        await fetchAlbums();
      } else {
        // Удаление фото из старой галереи
        for (const photoId of selectedPhotos) {
          await axios.delete(`${API_BASE_URL}/api/gallery/${photoId}`);
          if (homepageMainImageId === photoId) {
            try {
              await axios.delete(`${API_BASE_URL}/api/homepage/main-image`);
              setHomepageMainImageId(null);
            } catch (error) {
              console.error('Error removing homepage main image:', error);
            }
          }
        }
        await fetchPhotoItems();
      }
      setSelectedPhotos([]);
      setSelectAllMode(false);
      alert(t('admin.photosDeleted') || 'Фото удалены!');
    } catch (error) {
      console.error('Error deleting photos:', error);
      alert(t('admin.photoDeleteError') || 'Ошибка удаления фото');
    }
  };

  const handleBulkMovePhotos = async (targetAlbumId) => {
    if (selectedPhotos.length === 0 || !selectedAlbum) {
      alert(t('admin.selectPhotosToMove') || 'Выберите фото для переноса');
      return;
    }

    if (!targetAlbumId || targetAlbumId === selectedAlbum) {
      alert(t('admin.selectTargetAlbum') || 'Выберите целевой альбом');
      return;
    }

    try {
      for (const photoId of selectedPhotos) {
        // Получаем данные фото
        const album = albums.find(a => a.id === selectedAlbum);
        const photo = album?.photos.find(p => p.id === photoId);
        
        if (photo) {
          // Обновляем фото с новым альбомом
          await axios.put(`${API_BASE_URL}/api/albums/${selectedAlbum}/photos/${photoId}`, {
            title: photo.title,
            description: photo.description || '',
            newAlbumId: targetAlbumId
          });
        }
      }
      
      await fetchAlbums();
      setSelectedPhotos([]);
      setSelectAllMode(false);
      setShowMoveDialog(false);
      alert(t('admin.photosMoved') || 'Фото перенесены!');
    } catch (error) {
      console.error('Error moving photos:', error);
      alert(t('admin.photoMoveError') || 'Ошибка переноса фото');
    }
  };

  return (
    <div className="admin-section">
      {/* Albums Section */}
      <div style={{ marginBottom: '2rem' }}>
        {!selectedAlbum && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2>{t('admin.photoAlbums') || 'Photo Albums'}</h2>
          </div>
        )}
        
        {showAlbumForm && (
          <form onSubmit={handleCreateAlbum} className="upload-form" style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
            <input
              type="text"
              id="album-name"
              placeholder={t('admin.albumName') || 'Название альбома (например: Tomorrowland 2026)'}
              required
            />
            <textarea
              id="album-description"
              placeholder={t('admin.albumDescription') || 'Описание альбома (необязательно)'}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit">{t('admin.create') || 'Создать'}</button>
              <button type="button" onClick={() => setShowAlbumForm(false)} style={{ background: 'rgba(149, 165, 166, 0.2)', color: '#95a5a6' }}>
                {t('admin.cancel')}
              </button>
            </div>
          </form>
        )}

        {selectedAlbum ? (
          <div>
            {(() => {
              const album = albums.find(a => a.id === selectedAlbum);
              if (!album) return null;
              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <h3>{album.name}</h3>
                      {album.description && <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{album.description}</p>}
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                        {album.photos.length} {album.photos.length === 1 ? (t('admin.photo') || 'фото') : (t('admin.photos') || 'фото')}
                      </p>
                    </div>
                    <button onClick={() => setSelectedAlbum(null)} style={{ padding: '0.5rem 1rem', background: 'rgba(149, 165, 166, 0.2)', color: '#95a5a6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                      ← {t('admin.backToAlbums') || 'Back to albums'}
                    </button>
                  </div>
                  
                  {/* VK-style upload area */}
                  <div style={{ 
                    border: '2px dashed rgba(79, 172, 254, 0.3)', 
                    borderRadius: '12px', 
                    padding: '2rem', 
                    textAlign: 'center',
                    marginBottom: '2rem',
                    background: 'rgba(79, 172, 254, 0.05)',
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(79, 172, 254, 0.6)'; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(79, 172, 254, 0.3)'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'rgba(79, 172, 254, 0.3)';
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      handleUploadPhotosToAlbum(selectedAlbum, e.dataTransfer.files);
                    }
                  }}
                  >
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <div style={{ 
                        fontSize: '3rem', 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem',
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.25), rgba(155, 89, 182, 0.25))',
                        borderRadius: '50%',
                        boxShadow: '0 4px 20px rgba(79, 172, 254, 0.4), inset 0 0 20px rgba(79, 172, 254, 0.1)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '2px solid rgba(79, 172, 254, 0.4)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                        e.currentTarget.style.boxShadow = '0 6px 25px rgba(79, 172, 254, 0.6), inset 0 0 25px rgba(79, 172, 254, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(79, 172, 254, 0.4), inset 0 0 20px rgba(79, 172, 254, 0.1)';
                      }}
                      >
                        <FaImage style={{ 
                          color: '#4facfe', 
                          filter: 'drop-shadow(0 2px 6px rgba(79, 172, 254, 0.6))',
                          fontSize: '2.5rem'
                        }} />
                      </div>
                      <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: '#fff', fontWeight: '600', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                        {t('admin.uploadPhotos') || 'Загрузить фото в альбом'}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                        {t('admin.dragDropOrClick') || 'Перетащите фото сюда или нажмите для выбора'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleUploadPhotosToAlbum(selectedAlbum, e.target.files);
                          }
                        }}
                      />
                    </label>
                  </div>
                  {/* Панель управления массовым удалением */}
                  {album.photos.length > 0 && (
                    <div className="bulk-actions-panel">
                      <div className="bulk-actions-left">
                        <input
                          type="checkbox"
                          checked={selectAllMode && selectedPhotos.length === album.photos.length}
                          onChange={handleSelectAllPhotos}
                          className="bulk-checkbox"
                        />
                        <span className="bulk-selection-text">
                          {selectedPhotos.length > 0 ? `Выбрано: ${selectedPhotos.length}` : 'Выбрать все'}
                        </span>
                      </div>
                      {selectedPhotos.length > 0 && (
                        <div className="bulk-actions-right">
                          <button
                            onClick={() => setShowMoveDialog(true)}
                            className="move-btn"
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(79, 172, 254, 0.15)',
                              color: '#4facfe',
                              border: '1px solid rgba(79, 172, 254, 0.3)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontWeight: '500',
                              fontSize: '0.9rem'
                            }}
                          >
                            <FaArrowRight /> Перенести ({selectedPhotos.length})
                          </button>
                          <button
                            onClick={handleBulkDeletePhotos}
                            className="delete-btn-bulk"
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(231, 76, 60, 0.15)',
                              color: '#e74c3c',
                              border: '1px solid rgba(231, 76, 60, 0.3)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontWeight: '500',
                              fontSize: '0.9rem'
                            }}
                          >
                            <FaTrash /> Удалить ({selectedPhotos.length})
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="items-list">
                    {album.photos.map((photo) => (
                      <motion.div key={photo.id} className="item-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        {editingAlbumPhoto && editingAlbumPhoto.photoId === photo.id ? (
                          <div className="edit-form">
                            <input
                              type="text"
                              defaultValue={photo.title}
                              id={`album-photo-title-${photo.id}`}
                              placeholder={t('admin.titleField') || 'Название'}
                              required
                            />
                            <textarea
                              defaultValue={photo.description}
                              id={`album-photo-description-${photo.id}`}
                              placeholder={t('admin.description') || 'Описание'}
                            />
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                              {t('admin.moveToAlbum') || 'Перенести в альбом (необязательно)'}
                            </label>
                            <select
                              id={`album-photo-album-${photo.id}`}
                              defaultValue={selectedAlbum}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '1rem',
                                marginBottom: '1rem'
                              }}
                            >
                              <option value={selectedAlbum}>{t('admin.keepInCurrentAlbum') || 'Оставить в текущем альбоме'}</option>
                              {albums.filter(a => a.id !== selectedAlbum).map(a => (
                                <option key={a.id} value={a.id}>
                                  {a.name} ({a.photos.length} {t('admin.photos') || 'фото'})
                                </option>
                              ))}
                            </select>
                            <div className="edit-actions">
                              <button
                                className="save-btn"
                                onClick={() => handleSaveAlbumPhoto(selectedAlbum, photo.id)}
                              >
                                <FaSave /> {t('admin.save') || 'Сохранить'}
                              </button>
                              <button
                                className="cancel-btn"
                                onClick={() => setEditingAlbumPhoto(null)}
                              >
                                <FaTimes /> {t('admin.cancel') || 'Отмена'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="item-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem' }}>
                              <input
                                type="checkbox"
                                checked={selectedPhotos.includes(photo.id)}
                                onChange={() => handleTogglePhotoSelection(photo.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
                              />
                              <div className="item-thumbnail">
                                <img src={`${API_BASE_URL}${photo.url}`} alt={photo.title} className="gallery-item-thumbnail" />
                              </div>
                            </div>
                            <div className="item-footer" style={{ justifyContent: 'flex-end' }}>
                              <div className="item-actions">
                                <button
                                  className="edit-btn"
                                  onClick={() => setEditingAlbumPhoto({ photoId: photo.id, albumId: selectedAlbum })}
                                  title={t('admin.edit') || 'Редактировать'}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="delete-btn"
                                  onClick={() => handleDeletePhotoFromAlbum(selectedAlbum, photo.id)}
                                  title={t('admin.delete') || 'Удалить'}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </motion.div>
                    ))}
                    {album.photos.length === 0 && (
                      <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>
                        {t('admin.noPhotos') || 'В альбоме пока нет фото'}
                      </p>
                    )}
                  </div>
                  {/* Диалог переноса фото для альбома */}
                  {showMoveDialog && selectedPhotos.length > 0 && selectedAlbum && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}
                    onClick={() => setShowMoveDialog(false)}
                    >
                      <div style={{
                        background: 'rgba(30, 30, 30, 0.95)',
                        padding: '2rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        minWidth: '400px',
                        maxWidth: '500px'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      >
                        <h3 style={{ marginBottom: '1rem', color: '#fff' }}>
                          Перенести {selectedPhotos.length} {selectedPhotos.length === 1 ? 'фото' : 'фото'} в альбом
                        </h3>
                        <select
                          id="bulk-move-target-album"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '1rem',
                            marginBottom: '1rem'
                          }}
                        >
                          <option value="">Выберите альбом</option>
                          {albums.filter(a => a.id !== selectedAlbum).map(a => (
                            <option key={a.id} value={a.id}>
                              {a.name} ({a.photos.length} {t('admin.photos') || 'фото'})
                            </option>
                          ))}
                        </select>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setShowMoveDialog(false)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(149, 165, 166, 0.2)',
                              color: '#95a5a6',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            Отмена
                          </button>
                          <button
                            onClick={() => {
                              const targetAlbumId = document.getElementById('bulk-move-target-album').value;
                              handleBulkMovePhotos(targetAlbumId);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(79, 172, 254, 0.3)',
                              color: '#4facfe',
                              border: '1px solid rgba(79, 172, 254, 0.4)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            Перенести
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="items-list">
            {albums.map((album) => (
              <motion.div key={album.id} className="item-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {editingAlbum === album.id ? (
                  <div className="edit-form">
                    <input
                      type="text"
                      defaultValue={album.name}
                      id={`album-name-${album.id}`}
                      placeholder={t('admin.albumName') || 'Название альбома'}
                    />
                    <textarea
                      defaultValue={album.description || ''}
                      id={`album-description-${album.id}`}
                      placeholder={t('admin.albumDescription') || 'Описание альбома (необязательно)'}
                    />
                    <div className="edit-actions">
                      <button
                        className="save-btn"
                        onClick={() => handleUpdateAlbum(album.id)}
                      >
                        <FaSave /> {t('admin.save') || 'Сохранить'}
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => setEditingAlbum(null)}
                      >
                        <FaTimes /> {t('admin.cancel') || 'Отмена'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="item-info">
                      {album.coverPhoto && (
                        <div className="item-thumbnail">
                          <img src={`${API_BASE_URL}${album.coverPhoto}`} alt={album.name} className="gallery-item-thumbnail" />
                        </div>
                      )}
                      <div className="item-text-info">
                        <h3>{album.name}</h3>
                        {album.description && <p>{album.description}</p>}
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                          {album.photos.length} {album.photos.length === 1 ? (t('admin.photo') || 'фото') : (t('admin.photos') || 'фото')}
                        </p>
                      </div>
                    </div>
                    <div className="item-footer">
                      <div className="item-actions">
                        <button className="edit-btn" onClick={() => setSelectedAlbum(album.id)}>
                          <FaFolderOpen /> {t('admin.open') || 'Открыть'}
                        </button>
                        <button 
                          className="edit-btn" 
                          onClick={() => setEditingAlbum(album.id)}
                          title={t('admin.edit') || 'Редактировать'}
                        >
                          <FaEdit />
                        </button>
                        <button className="delete-btn" onClick={() => handleDeleteAlbum(album.id)} title={t('admin.delete') || 'Удалить'}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
            {albums.length === 0 && (
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>
                {t('admin.noAlbums') || 'Альбомов пока нет. Создайте первый альбом!'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Photo Upload Section */}
      <div style={{ marginTop: '3rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '2rem' }}>
        <h2>{t('admin.uploadPhotos') || 'Загрузка Фото'}</h2>
        <form onSubmit={handlePhotoUpload} className="upload-form">
          {/* Выбор альбома или создание нового */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
              {t('admin.selectAlbum') || 'Выберите альбом'} <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>({t('admin.autoCreateIfEmpty') || 'или будет создан автоматически'})</span>
            </label>
            <select
              value={createNewAlbumForUpload ? 'new' : uploadToAlbumId}
              onChange={(e) => {
                if (e.target.value === 'new') {
                  setCreateNewAlbumForUpload(true);
                  setUploadToAlbumId('');
                } else if (e.target.value === '') {
                  setCreateNewAlbumForUpload(false);
                  setUploadToAlbumId('');
                } else {
                  setCreateNewAlbumForUpload(false);
                  setUploadToAlbumId(e.target.value);
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <option value="">{t('admin.autoCreateAlbum') || 'Создать альбом автоматически'}</option>
              <option value="new">{t('admin.createNewAlbum') || '+ Создать новый альбом'}</option>
              {albums.map(album => (
                <option key={album.id} value={album.id}>
                  {album.name} ({album.photos.length} {t('admin.photos') || 'фото'})
                </option>
              ))}
            </select>

            {/* Форма создания нового альбома */}
            {createNewAlbumForUpload && (
              <div style={{ 
                padding: '1rem', 
                background: 'rgba(46, 204, 113, 0.1)', 
                borderRadius: '8px', 
                border: '1px solid rgba(46, 204, 113, 0.3)',
                marginBottom: '1rem'
              }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {t('admin.albumName') || 'Название альбома'} *
                </label>
                <input
                  type="text"
                  value={newAlbumNameForUpload}
                  onChange={(e) => setNewAlbumNameForUpload(e.target.value)}
                  placeholder={t('admin.albumName') || 'Название альбома (например: Tomorrowland 2026)'}
                  required={createNewAlbumForUpload}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    marginBottom: '0.75rem'
                  }}
                />
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {t('admin.albumDescription') || 'Описание альбома (необязательно)'}
                </label>
                <textarea
                  value={newAlbumDescriptionForUpload}
                  onChange={(e) => setNewAlbumDescriptionForUpload(e.target.value)}
                  placeholder={t('admin.albumDescription') || 'Описание альбома (необязательно)'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}
          </div>


          <label className="file-input-label" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <span style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(79, 172, 254, 0.1)',
              marginRight: '0.75rem',
              border: '1px solid rgba(79, 172, 254, 0.2)',
              flexShrink: 0
            }}>
              <FaImage style={{ color: '#4facfe', fontSize: '1.1rem' }} />
            </span>
            <span style={{ flex: 1 }}>{t('admin.selectImages')}</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageFilesChange}
            />
            {photoForm.images.length > 0 && (
              <span>{photoForm.images.length} {photoForm.images.length === 1 ? t('admin.file') : t('admin.files')}</span>
            )}
          </label>
          {photoForm.images.length > 0 && (
            <div className="selected-images-preview">
              {imagePreviews.map((item, index) => (
                <div key={index} className="image-preview-item">
                  <img 
                    src={item.preview} 
                    alt={item.file.name}
                    className="image-preview-thumbnail"
                  />
                  <div className="image-preview-info">
                    <span className="image-preview-name">{item.file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = photoForm.images.filter((_, i) => i !== index);
                        const newPreviews = imagePreviews.filter((_, i) => i !== index);
                        setPhotoForm({ ...photoForm, images: newImages });
                        setImagePreviews(newPreviews);
                      }}
                      className="remove-image-btn"
                      title={t('admin.delete')}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button type="submit" disabled={uploading}>
            {uploading 
              ? t('admin.uploading') 
              : `${t('admin.upload') || 'Загрузить'} ${photoForm.images.length > 0 ? `(${photoForm.images.length})` : ''}`
            }
          </button>
        </form>

        {/* Список элементов старой галереи (только если есть элементы) */}
        {photoItems.length > 0 && (
          <>
            <h2>{t('admin.photoItems') || 'Фото'}</h2>
            {/* Панель управления массовым удалением */}
            <div className="bulk-actions-panel">
              <div className="bulk-actions-left">
                <input
                  type="checkbox"
                  checked={selectAllMode && selectedPhotos.length === photoItems.length}
                  onChange={handleSelectAllPhotos}
                  className="bulk-checkbox"
                />
                <span className="bulk-selection-text">
                  {selectedPhotos.length > 0 ? `Выбрано: ${selectedPhotos.length}` : 'Выбрать все'}
                </span>
              </div>
              {selectedPhotos.length > 0 && (
                <div className="bulk-actions-right">
                  {albums.length > 0 && (
                    <button
                      onClick={() => setShowMoveDialog(true)}
                      className="move-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(79, 172, 254, 0.15)',
                        color: '#4facfe',
                        border: '1px solid rgba(79, 172, 254, 0.3)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontWeight: '500',
                        fontSize: '0.9rem'
                      }}
                    >
                      <FaArrowRight /> Перенести ({selectedPhotos.length})
                    </button>
                  )}
                  <button
                    onClick={handleBulkDeletePhotos}
                    className="delete-btn-bulk"
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(231, 76, 60, 0.15)',
                      color: '#e74c3c',
                      border: '1px solid rgba(231, 76, 60, 0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontWeight: '500',
                      fontSize: '0.9rem'
                    }}
                  >
                    <FaTrash /> Удалить ({selectedPhotos.length})
                  </button>
                </div>
              )}
            </div>
            {/* Диалог переноса фото для старой галереи */}
            {showMoveDialog && selectedPhotos.length > 0 && !selectedAlbum && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setShowMoveDialog(false)}
              >
                <div style={{
                  background: 'rgba(30, 30, 30, 0.95)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  minWidth: '400px',
                  maxWidth: '500px'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <h3 style={{ marginBottom: '1rem', color: '#fff' }}>
                    Перенести {selectedPhotos.length} {selectedPhotos.length === 1 ? 'фото' : 'фото'} в альбом
                  </h3>
                  <select
                    id="bulk-move-target-album-legacy"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      marginBottom: '1rem'
                    }}
                  >
                    <option value="">Выберите альбом</option>
                    {albums.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.photos.length} {t('admin.photos') || 'фото'})
                      </option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowMoveDialog(false)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(149, 165, 166, 0.2)',
                        color: '#95a5a6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={async () => {
                        const targetAlbumId = document.getElementById('bulk-move-target-album-legacy').value;
                        if (!targetAlbumId) {
                          alert('Выберите альбом');
                          return;
                        }
                        try {
                          // Для старой галереи нужно сначала получить данные фото, затем создать их в новом альбоме
                          for (const photoId of selectedPhotos) {
                            const photo = photoItems.find(p => p.id === photoId);
                            if (photo) {
                              // Создаём фото в новом альбоме
                              const formData = new FormData();
                              // Нужно получить файл фото - для этого можно использовать fetch
                              const response = await fetch(`${API_BASE_URL}${photo.url}`);
                              const blob = await response.blob();
                              formData.append('photos', blob, photo.title || 'photo.jpg');
                              
                              await axios.post(`${API_BASE_URL}/api/albums/${targetAlbumId}/photos`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              // Удаляем из старой галереи
                              await axios.delete(`${API_BASE_URL}/api/gallery/${photoId}`);
                              if (homepageMainImageId === photoId) {
                                try {
                                  await axios.delete(`${API_BASE_URL}/api/homepage/main-image`);
                                  setHomepageMainImageId(null);
                                } catch (error) {
                                  console.error('Error removing homepage main image:', error);
                                }
                              }
                            }
                          }
                          await fetchPhotoItems();
                          await fetchAlbums();
                          setSelectedPhotos([]);
                          setSelectAllMode(false);
                          setShowMoveDialog(false);
                          alert(t('admin.photosMoved') || 'Фото перенесены!');
                        } catch (error) {
                          console.error('Error moving photos:', error);
                          alert(t('admin.photoMoveError') || 'Ошибка переноса фото');
                        }
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(79, 172, 254, 0.3)',
                        color: '#4facfe',
                        border: '1px solid rgba(79, 172, 254, 0.4)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Перенести
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="items-list">
              {photoItems.map((item) => (
                <motion.div
                  key={item.id}
                  className="item-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {editingPhotoItem === item.id ? (
                    <div className="edit-form">
                      <input
                        type="text"
                        defaultValue={item.title}
                        id={`photo-title-${item.id}`}
                        placeholder={t('admin.titleField')}
                      />
                      <textarea
                        defaultValue={item.description}
                        id={`photo-description-${item.id}`}
                        placeholder={t('admin.description')}
                      />
                      <div className="edit-actions">
                        <button
                          className="save-btn"
                          onClick={() => handleSavePhotoItem(item.id)}
                        >
                          <FaSave /> {t('admin.save')}
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => setEditingPhotoItem(null)}
                        >
                          <FaTimes /> {t('admin.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="item-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedPhotos.includes(item.id)}
                          onChange={() => handleTogglePhotoSelection(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div className="item-thumbnail">
                          <img 
                            src={`${API_BASE_URL}${item.url}`} 
                            alt={item.title}
                            className="gallery-item-thumbnail"
                          />
                        </div>
                      </div>
                      <div className="item-footer" style={{ justifyContent: 'flex-end' }}>
                        <div className="item-actions">
                          <button
                            className={`homepage-btn ${homepageMainImageId === item.id ? 'active' : ''}`}
                            onClick={() => handleSetHomepageMainImage(item.id)}
                            title={homepageMainImageId === item.id ? (t('admin.isHomepageImage') || 'Уже установлено как главное фото') : (t('admin.setHomepageImage') || 'Установить как главное фото')}
                          >
                            <FaHome /> {homepageMainImageId === item.id ? (t('admin.homepageImage') || 'Главное фото') : ''}
                          </button>
                          <button
                            className="edit-btn"
                            onClick={() => setEditingPhotoItem(item.id)}
                            title={t('admin.edit')}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeletePhotoItem(item.id)}
                            title={t('admin.delete')}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhotoManager;

